/**
 * REST-клиент серверных уведомлений (/api/v1/notifications*).
 */

import { del, get, post, put } from "@/lib/api-client-v2";

export type MultilangText = { ru: string; [lang: string]: string };

export type UserNotificationListItem = {
  notification_id: number;
  type: "system" | "admin";
  scope: string;
  title: MultilangText | Record<string, string>;
  body: MultilangText | Record<string, string>;
  data?: Record<string, unknown> | null;
  read_at?: string | null;
  delivered_at?: string | null;
  created_at: string;
};

export type PreferenceItem = {
  scope: string;
  inapp: boolean;
  email: boolean;
  push: boolean;
};

export type DeviceListItem = {
  id: number;
  endpoint: string;
  last_used_at?: string | null;
  created_at: string;
};

export type NotificationAdmin = {
  id: number;
  type: "system" | "admin";
  status: "draft" | "processing" | "sent" | "archived";
  title: Record<string, string>;
  body: Record<string, string>;
  data?: Record<string, unknown> | null;
  scope: string;
  send_at?: string | null;
  created_at: string;
};

export type NotificationTemplate = {
  id: number;
  slug: string;
  title: Record<string, string>;
  body: Record<string, string>;
  scope: string;
  variables?: Record<string, string> | null;
  created_at: string;
};

export type ScopeCatalogItem = { code: string; label: string };

export type RecipientSearchItem = {
  id: string;
  email?: string | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

export type SendMode = "all" | "users" | "roles";

export function pickLang(
  texts: Record<string, string> | null | undefined,
  preferred = "ru",
): string {
  if (!texts) return "";
  return texts[preferred] ?? texts.ru ?? texts.en ?? Object.values(texts)[0] ?? "";
}

export const notificationsApi = {
  list: (params?: { unread_only?: boolean; limit?: number; offset?: number }) => {
    const q: Record<string, string | number | boolean> = {};
    if (params?.unread_only != null) q.unread_only = params.unread_only;
    if (params?.limit != null) q.limit = params.limit;
    if (params?.offset != null) q.offset = params.offset;
    return get<UserNotificationListItem[]>("/notifications", q);
  },

  unreadCount: () => get<{ count: number }>("/notifications/unread-count"),

  markRead: (notificationId: number) =>
    put<void>(`/notifications/${notificationId}/read`),

  markAllRead: () => put<void>("/notifications/read-all"),

  getPreferences: () => get<{ items: PreferenceItem[] }>("/notifications/preferences"),

  putPreferences: (items: PreferenceItem[]) =>
    put<{ items: PreferenceItem[] }>("/notifications/preferences", { items }),

  listDevices: () => get<DeviceListItem[]>("/notifications/devices"),

  registerDevice: (body: { endpoint: string; p256dh: string; auth: string }) =>
    post<DeviceListItem>("/notifications/devices", body),

  deleteDevice: (id: number) => del(`/notifications/devices/${id}`),

  pushVapid: () => get<{ vapid_public_key: string }>("/notifications/push-vapid"),

  sseToken: () => get<{ token: string; expires_in: number }>("/notifications/sse-token"),

  // admin
  adminList: (params?: { status?: string; scope?: string; limit?: number; offset?: number }) => {
    const q: Record<string, string | number | boolean> = {};
    if (params?.status) q.status = params.status;
    if (params?.scope) q.scope = params.scope;
    if (params?.limit != null) q.limit = params.limit;
    if (params?.offset != null) q.offset = params.offset;
    return get<NotificationAdmin[]>("/admin/notifications", q);
  },

  adminCreate: (body: {
    type?: string;
    title: MultilangText;
    body: MultilangText;
    scope: string;
    data?: Record<string, unknown>;
    send_at?: string | null;
  }) => post<NotificationAdmin>("/admin/notifications", body),

  adminUpdate: (id: number, body: Record<string, unknown>) =>
    put<NotificationAdmin>(`/admin/notifications/${id}`, body),

  adminDelete: (id: number) => del(`/admin/notifications/${id}`),

  adminBulkDelete: (ids: number[]) =>
    post<{ deleted: number; skipped_processing: number }>("/admin/notifications/bulk-delete", { ids }),

  adminSend: (
    id: number,
    body?: { mode: SendMode; user_ids?: string[]; role_ids?: number[] },
  ) =>
    post<{
      notification_id: number;
      status: string;
      queued_batches: number;
      recipient_count: number;
      deferred: boolean;
    }>(`/admin/notifications/${id}/send`, body ?? { mode: "all" }),

  adminStats: (id: number) =>
    get<{ total: number; delivered: number; read: number }>(`/admin/notifications/${id}/stats`),

  scopes: () => get<ScopeCatalogItem[]>("/admin/notifications/scopes"),

  recipients: (q: string) =>
    get<RecipientSearchItem[]>("/admin/notifications/recipients", { q }),

  templatesList: () => get<NotificationTemplate[]>("/admin/notification-templates"),

  templatesCreate: (body: {
    slug: string;
    title: MultilangText;
    body: MultilangText;
    scope: string;
    variables?: Record<string, string>;
  }) => post<NotificationTemplate>("/admin/notification-templates", body),

  templatesUpdate: (id: number, body: Record<string, unknown>) =>
    put<NotificationTemplate>(`/admin/notification-templates/${id}`, body),

  templatesDelete: (id: number) => del(`/admin/notification-templates/${id}`),

  templatesRender: (id: number, body: { language?: string; variables?: Record<string, unknown> }) =>
    post<{ title: string; body: string; language: string }>(
      `/admin/notification-templates/${id}/render`,
      body,
    ),

  templatesSend: (
    id: number,
    body: { variables?: Record<string, unknown>; user_ids?: string[]; send_at?: string },
  ) =>
    post<{ notification_id: number; status: string }>(
      `/admin/notification-templates/${id}/send`,
      body,
    ),

  templatesSendSystem: (
    id: number,
    body: { user_id: string; variables?: Record<string, unknown> },
  ) =>
    post<{ notification_id: number; status: string }>(
      `/admin/notification-templates/${id}/send-system`,
      body,
    ),
};
